import * as React from 'react';
import { Component } from 'react';
import { AppUser } from '../../../../../../views/App/App';
import propz from 'propz';
import { History, Location } from 'history';
import { CreateGeneralMessageButtons } from '../../../../../../components/CreateGeneralMessageButtons/CreateGeneralMessageButtons';
import { GeneralMessageSettingsForm } from '../../../../../../components/GeneralMessageSettingsForm/GeneralMessageSettingsForm';
import { GeneralMessageTextForm } from '../../../../../../components/GeneralMessageTextForm/GeneralMessageTextForm';
import { CreateGeneralMessageStudentsStep } from '../../../../../../components/CreateGeneralMessageSteps/CreateGeneralMessageStudentsStep';
import { CreateGeneralMessageStaffStep } from '../../../../../../components/CreateGeneralMessageSteps/CreateGeneralMessageStaffStep';
import { CreateGeneralMessageGovernorsStep } from '../../../../../../components/CreateGeneralMessageSteps/CreateGeneralMessageGovernorsStep';
import { CreateGeneralMessageRecipientsStepV2 } from '../../../../../../components/CreateGeneralMessageRecipientsStepV2/CreateGeneralMessageRecipientsStepV2';
import { GeneralMessagePreviewWithTabs } from '../../../../../../components/GeneralMessagePreview/GeneralMessagesPreviewWithTabs';
import { AffectedUserList } from '../../../../../../components/GeneralMessages/AffectedUserList/AffectedUserList';
import { ACTION_TYPE } from '../../../../../../components/GeneralMessageActionsButton/GeneralMessageActionsButton';
import {
  HTML_FIRST_PART_PAGE_CODE,
  HTML_LAST_PART_PAGE_CODE
} from '../../../../../../components/HTMLEditorQuill/HTMLEditorConst';
import { ScheduleSendingForm } from '../../../../../../components/GeneralMessages/ScheduleSendingForm/ScheduleSendingForm';
import { ProgressBar } from '../../../../../../components/ProgressBar/ProgressBar';
import {
  createSchoolGeneralMessage,
  getSchoolGeneralMessageForEditor,
  getGeneralMessageSelectedRecipientsCount,
  getGeneralMessageSelectedRecipientsPreview,
  scheduleSchoolGeneralMessage,
  sendSchoolGeneralMessage,
  updateSchoolGeneralMessage,
  resolveGeneralMessageRecipients,
  GeneralMessageRecipientChannelSummary,
  GeneralMessageRecipientSelection,
  GeneralMessageRecipientSelectionItem,
  GeneralMessageRecipientType
} from '../../../../../../helpers/service/admin/generalMessage';
import { uploadFileAllPath } from '../../../../../../helpers/service/file';
import {
  DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
  getGeneralMessageAttachmentContentHash,
  getGeneralMessageAttachmentUploadError,
  getGeneralMessageDuplicateAttachmentError,
  getGeneralMessageRequestError,
  isGeneralMessageAttachmentDuplicate,
  validateGeneralMessageAttachmentAddition,
  validateGeneralMessageAttachmentCollection
} from '../../../../../../helpers/generalMessageAttachments/generalMessageAttachmentSize';
import { TextUtils } from '../../../../../../helpers/utils/TextUtils';
import { getSettingsUploadFiles } from '../../../../../../helpers/service/nobody/settings';
import {
  Attachment,
  GeneralMessageCopyOptions,
  ParentsDeliverySettings
} from '../../../../../../models/generalMessage';
import { SchoolStudent } from '../../../../../../models/schoolStudent';
import { SchoolUser } from '../../../../../../models/schoolUser';
import { HTMLEditorQuillHandle } from '../../../../../../components/HTMLEditorQuill/HTMLEditorQuill';
import { stripVariableChips } from '../../../../../../components/HTMLEditorQuill/HTMLEditorQuill';
import { GeneralMessageContentV2 } from '../../../../../../components/GeneralMessageContentV2/GeneralMessageContentV2';
import { VariableTextInputHandle } from '../../../../../../components/VariableTextInput/VariableTextInput';
import { GeneralMessagesUnavailableModal } from '../../../../../../components/GeneralMessagesUnavailableModal/GeneralMessagesUnavailableModal';
import { UnsavedChangesModal } from '../../../../../../components/UnsavedChangesModal/UnsavedChangesModal';
import {
  GeneralMessageRecipientSelectionState,
  GeneralMessageRecipientSelectionStateGroup,
  getEmptyGeneralMessageRecipientSelectionState,
  getGeneralMessageRecipientSelectionForSave,
  getGeneralMessageRecipientSelectionGroupSize,
  updateGeneralMessageRecipientSelectionStateGroup
} from '../../../../../../helpers/generalMessageRecipientSelection/generalMessageRecipientSelection';
import {
  PageHelpDefinition,
  PageHelpRegistration,
  registerPageHelpDefinition
} from '../../../../../../helpers/pageHelp/pageHelp';
import './CreateGeneralMessageView.css';

enum MESSAGE_CREATING_STEPS {
  SETTINGS = 'SETTINGS',
  STUDENT_LIST = 'STUDENT_LIST',
  STAFF_LIST = 'STAFF_LIST',
  GOVERNORS_LIST = 'GOVERNORS_LIST',
  CONTENT = 'CONTENT',
  PREVIEW = 'PREVIEW',
  AFFECTED_USERS = 'AFFECTED_USERS'
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  match?: {
    params?: {
      id?: string;
    };
  };
}

interface MessageState {
  messageId?: string;
  messageTitle: string;
  messageDescription: string;
  parentsDeliverySettings: ParentsDeliverySettings;
  isSendStudents: boolean;
  isSendParents: boolean;
  isSendStaff: boolean;
  isSendGovernors: boolean;
  messageSmsText: string;
  messageEmailSubject: string;
  messageEmailBody: string;
  messagePushTitle: string;
  messagePushBody: string;
  messageAttachments: Attachment[];
  selectedStudents: SchoolStudent[];
  selectedStaff: SchoolUser[];
  selectedGovernors: SchoolUser[];
  recipientSelection: GeneralMessageRecipientSelectionState;
  affectedUserList: any[];
  affectedUsersCount: number;
  affectedUserChannelSummary?: GeneralMessageRecipientChannelSummary;
  isResolvingRecipients: boolean;
  messageScheduleAt: string;
  isImageError: boolean;
  maxSizeUploadFile: number;
  isLoadingFile: boolean;

  isSmsEditorEnabled: boolean;
  isPushEditorEnabled: boolean;
  isEmailEditorEnabled: boolean;

  isLoadingAffectedUserList: boolean;

  senderDisplayName: string;
}

interface StepState {
  currentStep: number;
}

interface ErrorState {
  isError: boolean;
  errorMessage: string;
}

interface ScheduleFormState {
  isScheduleFormVisible: boolean;
}

interface FullState extends MessageState, StepState, ErrorState, ScheduleFormState {
  isStudentsSelectionCompleted: boolean;
  isStaffSelectionCompleted: boolean;
  isGovernorsSelectionCompleted: boolean;
  isStudentRecipientsPreselected: boolean;
  isAllUserRecipientsPreselected: boolean;
  allUserAudienceGroups: {
    students: boolean;
    parents: boolean;
    staffs: boolean;
    governors: boolean;
  };
  isStepChanging: boolean;
  isActionInProgress: boolean;
  isInitialLoading: boolean;
  isEditMode: boolean;
  loadedUpdatedAt?: string;
  lastSavedAt?: string;
  isUnsavedChangesModalOpen: boolean;
}

const DEFAULT_PARENTS_DELIVERY_SETTINGS: ParentsDeliverySettings = {
  all: false,
  firstPriority: true,
  secondPriority: true,
  thirdPriority: false
};

const DEFAULT_STATE: FullState = {
  currentStep: 0,

  isError: false,
  errorMessage: '',

  messageTitle: '',
  messageDescription: '',
  parentsDeliverySettings: DEFAULT_PARENTS_DELIVERY_SETTINGS,
  isSendParents: false,
  isSendStudents: false,
  isSendStaff: false,
  isSendGovernors: false,
  messageSmsText: '',
  messageEmailSubject: '',
  messageEmailBody: '',
  messagePushTitle: '',
  messagePushBody: '',
  messageAttachments: [],
  selectedStudents: [],
  selectedStaff: [],
  selectedGovernors: [],
  recipientSelection: getEmptyGeneralMessageRecipientSelectionState(),
  affectedUserList: [],
  affectedUsersCount: 0,
  affectedUserChannelSummary: undefined,
  isResolvingRecipients: false,
  messageScheduleAt: '',
  isSmsEditorEnabled: true,
  isPushEditorEnabled: true,
  isEmailEditorEnabled: true,
  isScheduleFormVisible: false,
  isLoadingFile: false,
  isImageError: false,
  maxSizeUploadFile: DEFAULT_GENERAL_MESSAGE_ATTACHMENT_SIZE_LIMIT_BYTES,
  isStudentsSelectionCompleted: false,
  isStaffSelectionCompleted: false,
  isGovernorsSelectionCompleted: false,
  isStudentRecipientsPreselected: false,
  isAllUserRecipientsPreselected: false,
  allUserAudienceGroups: {
    students: false,
    parents: false,
    staffs: false,
    governors: false
  },
  isStepChanging: false,
  isActionInProgress: false,
  isInitialLoading: false,
  isEditMode: false,
  loadedUpdatedAt: undefined,
  lastSavedAt: undefined,
  isUnsavedChangesModalOpen: false,

  isLoadingAffectedUserList: false,

  senderDisplayName: ''
};

interface GeneralMessageCopyContext {
  sourceMessage: any;
  copyOptions: GeneralMessageCopyOptions;
}

const getGeneralMessageCopyContext = (locationState: any): GeneralMessageCopyContext | undefined => {
  if (!locationState || locationState.isCreateLikeThis !== true) return undefined;

  const sourceMessage = locationState.sourceMessage || locationState.generalMessage || locationState.message;
  if (!sourceMessage) return undefined;

  const savedOptions = locationState.copyOptions;
  const copyOptions: GeneralMessageCopyOptions = savedOptions
    ? {
        title: Boolean(savedOptions.title),
        content: Boolean(savedOptions.content),
        recipients: Boolean(savedOptions.recipients)
      }
    : {
        // Keep old navigation state working while callers move to the single copy action.
        title: true,
        content: true,
        recipients: Boolean(locationState.isAllMessageCopy)
      };

  return { sourceMessage, copyOptions };
};

export class CreateGeneralMessageView extends Component<Props, FullState> {
  private savedDraftSnapshot = '';
  private unblockHistory?: () => void;
  private pendingNavigation?: { location: any; action: string };
  private isNavigationAllowed = false;
  private hasCopiedPrefill = false;
  private pendingAttachmentContentHashes = new Set<string>();
  private pageHelpRegistration?: PageHelpRegistration;
  private pageHelpSignature = '';
  private disabledChannelDrafts: {
    email?: { subject: string; body: string; attachments: Attachment[] };
    push?: { title: string; body: string };
    sms?: { text: string };
  } = {};

  private setEmailChannelEnabled = (isEnabled: boolean): void => {
    if (!isEnabled && this.state.isEmailEditorEnabled) {
      this.disabledChannelDrafts.email = {
        subject: this.state.messageEmailSubject,
        body: this.state.messageEmailBody,
        attachments: [...this.state.messageAttachments]
      };
      this.setState({
        isEmailEditorEnabled: false,
        messageEmailSubject: '',
        messageEmailBody: '',
        messageAttachments: []
      });
      return;
    }

    if (isEnabled && !this.state.isEmailEditorEnabled) {
      const cached = this.disabledChannelDrafts.email;
      this.disabledChannelDrafts.email = undefined;
      if (cached) {
        this.setState({
          isEmailEditorEnabled: true,
          messageEmailSubject: cached.subject,
          messageEmailBody: cached.body,
          messageAttachments: [...cached.attachments]
        });
      } else {
        this.setState({ isEmailEditorEnabled: true });
      }
    }
  };

  private setPushChannelEnabled = (isEnabled: boolean): void => {
    if (!isEnabled && this.state.isPushEditorEnabled) {
      this.disabledChannelDrafts.push = {
        title: this.state.messagePushTitle,
        body: this.state.messagePushBody
      };
      this.setState({ isPushEditorEnabled: false, messagePushTitle: '', messagePushBody: '' });
      return;
    }

    if (isEnabled && !this.state.isPushEditorEnabled) {
      const cached = this.disabledChannelDrafts.push;
      this.disabledChannelDrafts.push = undefined;
      if (cached) {
        this.setState({ isPushEditorEnabled: true, messagePushTitle: cached.title, messagePushBody: cached.body });
      } else {
        this.setState({ isPushEditorEnabled: true });
      }
    }
  };

  private setSmsChannelEnabled = (isEnabled: boolean): void => {
    if (!isEnabled && this.state.isSmsEditorEnabled) {
      this.disabledChannelDrafts.sms = { text: this.state.messageSmsText };
      this.setState({ isSmsEditorEnabled: false, messageSmsText: '' });
      return;
    }

    if (isEnabled && !this.state.isSmsEditorEnabled) {
      const cached = this.disabledChannelDrafts.sms;
      this.disabledChannelDrafts.sms = undefined;
      if (cached) {
        this.setState({ isSmsEditorEnabled: true, messageSmsText: cached.text });
      } else {
        this.setState({ isSmsEditorEnabled: true });
      }
    }
  };

  componentDidMount() {
    this.pageHelpRegistration = registerPageHelpDefinition(this.getPageHelpDefinition());
    this.pageHelpSignature = this.getPageHelpSignature();
    this.updateTopbarWizardHeader();
    getSettingsUploadFiles(this.props.user).then(maxSizeUploadFile => this.setState({ maxSizeUploadFile }));
    window.addEventListener('beforeunload', this.onBeforeUnload);
    this.unblockHistory = this.props.history.block((location, action) => {
      const isInternalQueryChange = location.pathname === this.props.history.location.pathname;
      if (this.isNavigationAllowed || isInternalQueryChange || !this.hasUnsavedChanges()) {
        return undefined;
      }

      this.pendingNavigation = { location, action };
      this.setState({ isUnsavedChangesModalOpen: true });
      return false;
    });

    const messageId = this.state.messageId;
    if (messageId) {
      this.loadDraftForEditing(messageId);
    } else {
      const copyContext = getGeneralMessageCopyContext(this.props.history.location.state);
      if (copyContext && copyContext.copyOptions.recipients) {
        this.loadCopiedAudience(copyContext.sourceMessage);
        return;
      }

      this.savedDraftSnapshot = this.getDraftSnapshot();
    }
  }

  componentDidUpdate() {
    this.updateTopbarWizardHeader();
    this.updatePageHelpDefinition();
  }

  componentWillUnmount() {
    if (this.pageHelpRegistration) this.pageHelpRegistration.unregister();
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    if (this.unblockHistory) {
      this.unblockHistory();
    }
    window.dispatchEvent(new CustomEvent('sit:general-message-wizard-header-clear'));
  }

  updateTopbarWizardHeader() {
    if (!this.isGeneralMessagesEnabledForSchool()) {
      window.dispatchEvent(new CustomEvent('sit:general-message-wizard-header-clear'));
      return;
    }

    const steps = this.getSteps();
    const currentStep = this.getCurrentStepIndex();

    window.dispatchEvent(
      new CustomEvent('sit:general-message-wizard-header', {
        detail: {
          steps: steps.map((step, index) => ({
            label: this.getStepTitleByName(step),
            state: index < currentStep ? 'done' : index === currentStep ? 'active' : 'pending'
          }))
        }
      })
    );
  }

  isGeneralMessagesEnabledForSchool(): boolean {
    return Boolean(propz.get(this.props.user, ['activeSchool', 'isGeneralMessagesEnabled'], true));
  }

  closeUnavailableModal = (): void => {
    const returnTo = propz.get(this.props.location, ['state', 'returnTo']);
    if (returnTo) {
      this.props.history.push(returnTo);
      return;
    }
    this.props.history.push('/generalMessages');
  };
  private editorRef = React.createRef<HTMLEditorQuillHandle>();
  private subjectInputRef = React.createRef<VariableTextInputHandle>();
  private fromInputRef = React.createRef<VariableTextInputHandle>();
  private pushTitleInputRef = React.createRef<HTMLInputElement>();
  private pushBodyInputRef = React.createRef<HTMLTextAreaElement>();
  private smsInputRef = React.createRef<HTMLTextAreaElement>();
  constructor(props: Props) {
    super(props);

    let initialState = { ...DEFAULT_STATE };
    const locationState = props.history.location.state as any;
    const editMessageId =
      propz.get(props, ['match', 'params', 'id']) ||
      new URLSearchParams(propz.get(props.location, ['search'], '')).get('draftId');

    if (editMessageId) {
      initialState.messageId = editMessageId;
      initialState.isEditMode = true;
      initialState.isInitialLoading = true;
    }

    if (typeof locationState !== 'undefined' && locationState.fromStudentsList === true) {
      const students = Array.isArray(locationState.students) ? locationState.students : [];
      const selectedStudents = students.filter(
        student => !TextUtils.isEmpty(student.id) && !TextUtils.isEmpty(student.permissionId)
      );

      initialState.isSendStudents = Boolean(locationState.sendToStudents);
      initialState.isSendParents = Boolean(locationState.sendToParents);
      initialState.selectedStudents = selectedStudents;
      initialState.isStudentsSelectionCompleted = selectedStudents.length > 0;
      initialState.isStudentRecipientsPreselected = selectedStudents.length > 0;
      initialState.recipientSelection = {
        ...initialState.recipientSelection,
        students: {
          ...initialState.recipientSelection.students,
          selected: selectedStudents.map(student => ({
            userId: student.id,
            permissionId: student.permissionId
          })),
          matchingCount: selectedStudents.length
        }
      };
    }
    if (typeof locationState !== 'undefined' && locationState.fromAllUsersList === true) {
      const users = Array.isArray(locationState.users) ? locationState.users : [];
      const students: any[] = [];
      const staffs: any[] = [];
      const governors: any[] = [];
      let hasParents = false;
      let hasStaffs = false;
      const seen = new Set<string>();

      users.forEach(schoolUser => {
        const userId = propz.get(schoolUser, ['id']);
        const permissions = propz.get(schoolUser, ['permissions'], []);

        permissions.forEach(permission => {
          const permissionId = propz.get(permission, ['id']);
          const preset = propz.get(permission, ['preset']);
          const key = `${userId || ''}:${permissionId || ''}`;

          if (TextUtils.isEmpty(userId) || TextUtils.isEmpty(permissionId) || seen.has(key)) {
            return;
          }
          seen.add(key);

          const recipient = {
            ...schoolUser,
            permissionId,
            permissions: [permission, ...permissions.filter(item => propz.get(item, ['id']) !== permissionId)]
          };
          if (preset === 'STUDENT') {
            students.push(recipient);
          } else if (preset === 'GOVERNOR') {
            governors.push(recipient);
          } else {
            // Manual selections are resolved by the exact user/permission pair. Keeping
            // parents here avoids expanding the audience to every parent linked to a child.
            staffs.push(recipient);
            if (preset === 'PARENT') {
              hasParents = true;
            } else {
              hasStaffs = true;
            }
          }
        });
      });

      const toSelection = recipients =>
        recipients.map(recipient => ({
          userId: recipient.id,
          permissionId: recipient.permissionId
        }));

      initialState.isSendStudents = students.length > 0;
      initialState.isSendStaff = staffs.length > 0;
      initialState.isSendGovernors = governors.length > 0;
      initialState.selectedStudents = students;
      initialState.selectedStaff = staffs;
      initialState.selectedGovernors = governors;
      initialState.isStudentsSelectionCompleted = students.length > 0;
      initialState.isStaffSelectionCompleted = staffs.length > 0;
      initialState.isGovernorsSelectionCompleted = governors.length > 0;
      initialState.isAllUserRecipientsPreselected = seen.size > 0;
      initialState.allUserAudienceGroups = {
        students: students.length > 0,
        parents: hasParents,
        staffs: hasStaffs,
        governors: governors.length > 0
      };
      initialState.recipientSelection = {
        ...initialState.recipientSelection,
        students: {
          ...initialState.recipientSelection.students,
          selected: toSelection(students),
          matchingCount: students.length
        },
        staffs: {
          ...initialState.recipientSelection.staffs,
          selected: toSelection(staffs),
          matchingCount: staffs.length
        },
        governors: {
          ...initialState.recipientSelection.governors,
          selected: toSelection(governors),
          matchingCount: governors.length
        }
      };
    }
    const copyContext = getGeneralMessageCopyContext(locationState);
    if (copyContext) {
      const { sourceMessage, copyOptions } = copyContext;
      this.hasCopiedPrefill = copyOptions.title || copyOptions.content || copyOptions.recipients;

      if (copyOptions.title) {
        initialState.messageTitle = propz.get(sourceMessage, ['title'], '');
        initialState.messageDescription = propz.get(sourceMessage, ['description'], '');
      }

      if (copyOptions.content) {
        const savedChannels = propz.get(sourceMessage, ['deliveryChannels']);
        const smsText = propz.get(sourceMessage, ['content', 'sms', 'text'], '');
        const emailSubject = propz.get(sourceMessage, ['content', 'email', 'subject'], '');
        const emailBody = this.extractEditableEmailBody(propz.get(sourceMessage, ['content', 'email', 'body'], ''));
        const attachments = propz.get(sourceMessage, ['content', 'email', 'attachments'], []);
        const pushTitle = propz.get(sourceMessage, ['content', 'push', 'title'], '');
        const pushBody = propz.get(sourceMessage, ['content', 'push', 'body'], '');

        initialState.isSmsEditorEnabled =
          typeof propz.get(savedChannels, ['sms']) === 'boolean'
            ? propz.get(savedChannels, ['sms'])
            : !TextUtils.isEmpty(smsText);
        initialState.isEmailEditorEnabled =
          typeof propz.get(savedChannels, ['email']) === 'boolean'
            ? propz.get(savedChannels, ['email'])
            : !TextUtils.isEmpty(emailSubject) || !TextUtils.isEmpty(emailBody) || attachments.length > 0;
        initialState.isPushEditorEnabled =
          typeof propz.get(savedChannels, ['push']) === 'boolean'
            ? propz.get(savedChannels, ['push'])
            : !TextUtils.isEmpty(pushTitle) || !TextUtils.isEmpty(pushBody);
        initialState.messageSmsText = initialState.isSmsEditorEnabled ? smsText : '';
        initialState.messageEmailSubject = initialState.isEmailEditorEnabled ? emailSubject : '';
        initialState.messageEmailBody = initialState.isEmailEditorEnabled ? emailBody : '';
        initialState.messageAttachments =
          initialState.isEmailEditorEnabled && Array.isArray(attachments) ? attachments : [];
        initialState.messagePushTitle = initialState.isPushEditorEnabled ? pushTitle : '';
        initialState.messagePushBody = initialState.isPushEditorEnabled ? pushBody : '';
        initialState.senderDisplayName = propz.get(sourceMessage, ['senderDisplayName'], '');
      }

      if (copyOptions.recipients) {
        initialState.isInitialLoading = true;
      }
    }

    this.state = initialState;
  }

  onBeforeUnload = (event: BeforeUnloadEvent): string | undefined => {
    if (!this.hasUnsavedChanges()) {
      return undefined;
    }

    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  persistDraftIdInLocation = (messageId: string): void => {
    if (propz.get(this.props, ['match', 'params', 'id'])) {
      return;
    }
    const params = new URLSearchParams(propz.get(this.props.location, ['search'], ''));
    params.set('draftId', messageId);
    this.props.history.replace({
      ...this.props.history.location,
      search: `?${params.toString()}`
    });
  };

  getDraftSnapshot = (): string => JSON.stringify(this.buildGeneralMessageData());

  hasUnsavedChanges = (): boolean => {
    if (this.state.isInitialLoading) {
      return false;
    }
    if (!this.state.messageId && this.hasCopiedPrefill) return true;
    if (!this.savedDraftSnapshot) return false;
    return this.getDraftSnapshot() !== this.savedDraftSnapshot;
  };

  navigateWithoutGuard = (navigate: () => void): void => {
    this.isNavigationAllowed = true;
    if (this.unblockHistory) {
      this.unblockHistory();
      this.unblockHistory = undefined;
    }
    navigate();
  };

  onKeepEditing = (): void => {
    this.pendingNavigation = undefined;
    this.setState({ isUnsavedChangesModalOpen: false });
  };

  onDiscardChanges = (): void => {
    const pendingNavigation = this.pendingNavigation;
    this.pendingNavigation = undefined;
    this.setState({ isUnsavedChangesModalOpen: false }, () => {
      this.navigateWithoutGuard(() => {
        if (!pendingNavigation) {
          this.goToMessagesList();
          return;
        }
        if (pendingNavigation.action === 'REPLACE') {
          this.props.history.replace(pendingNavigation.location);
        } else {
          this.props.history.push(pendingNavigation.location);
        }
      });
    });
  };

  normalizeSelectionGroup = (group: any): GeneralMessageRecipientSelectionStateGroup => {
    const mode: 'MANUAL' | 'FILTER' = propz.get(group, ['mode']) === 'FILTER' ? 'FILTER' : 'MANUAL';
    return {
      mode,
      all: Boolean(propz.get(group, ['all'], false)),
      selected: propz.get(group, ['selected'], []),
      excluded: propz.get(group, ['excluded'], []),
      filter: propz.get(group, ['filter'], {}),
      filters: propz.get(group, ['filters'], []),
      matchingCount: mode === 'FILTER' ? 0 : propz.get(group, ['selected', 'length'], 0),
      previewItems: []
    };
  };

  getSelectionGroupCount = (
    recipientType: GeneralMessageRecipientType,
    group: GeneralMessageRecipientSelectionStateGroup
  ): Promise<number> => {
    if (group.mode !== 'FILTER') {
      return Promise.resolve((group.selected || []).length);
    }

    return getGeneralMessageSelectedRecipientsCount(this.props.user, recipientType, {
      recipientSelection: { [recipientType]: group }
    }).then(result => propz.get(result, ['count'], 0));
  };

  hydrateRecipientSelectionGroup = (
    recipientType: GeneralMessageRecipientType,
    group: GeneralMessageRecipientSelectionStateGroup
  ): Promise<GeneralMessageRecipientSelectionStateGroup> => {
    if (group.mode === 'FILTER') {
      return this.getSelectionGroupCount(recipientType, group).then(matchingCount => ({
        ...group,
        matchingCount
      }));
    }

    if ((group.selected || []).length === 0) {
      return Promise.resolve({ ...group, matchingCount: 0, previewItems: [] });
    }

    return getGeneralMessageSelectedRecipientsPreview(this.props.user, recipientType, {
      recipientSelection: { [recipientType]: group }
    }).then(previewItems => ({
      ...group,
      selected: previewItems.map(item => ({ userId: item.userId, permissionId: item.permissionId })),
      matchingCount: previewItems.length,
      previewItems
    }));
  };

  getLegacySelectionItems = (items: any[]): GeneralMessageRecipientSelectionItem[] =>
    (Array.isArray(items) ? items : [])
      .map(item => ({
        userId: propz.get(item, ['userId']),
        permissionId: propz.get(item, ['permissionId'])
      }))
      .filter(item => !TextUtils.isEmpty(item.userId) && !TextUtils.isEmpty(item.permissionId));

  getLegacyRecipientGroup = (
    recipientType: GeneralMessageRecipientType,
    items: GeneralMessageRecipientSelectionItem[]
  ): Promise<GeneralMessageRecipientSelectionStateGroup> => {
    if (items.length === 0) {
      return Promise.resolve(this.normalizeSelectionGroup(undefined));
    }

    const group = this.normalizeSelectionGroup({ mode: 'MANUAL', selected: items });
    return getGeneralMessageSelectedRecipientsPreview(this.props.user, recipientType, {
      recipientSelection: { [recipientType]: group }
    }).then(preview => {
      const selected = preview.map(item => ({ userId: item.userId, permissionId: item.permissionId }));
      return {
        ...group,
        selected,
        matchingCount: selected.length,
        previewItems: preview
      };
    });
  };

  buildRecipientSelectionStateForMessage = async (message: any): Promise<GeneralMessageRecipientSelectionState> => {
    const savedSelection = propz.get(message, ['recipientSelection']);

    if (savedSelection) {
      const students = this.normalizeSelectionGroup(propz.get(savedSelection, ['students']));
      const staffs = this.normalizeSelectionGroup(propz.get(savedSelection, ['staffs']));
      const governors = this.normalizeSelectionGroup(propz.get(savedSelection, ['governors']));
      const groups = await Promise.all([
        this.hydrateRecipientSelectionGroup('students', students),
        this.hydrateRecipientSelectionGroup('staffs', staffs),
        this.hydrateRecipientSelectionGroup('governors', governors)
      ]);

      return {
        students: groups[0],
        staffs: groups[1],
        governors: groups[2]
      };
    }

    const studentItems = this.getLegacySelectionItems(propz.get(message, ['subscribers', 'lists', 'students'], []));
    const schoolStaffItems = this.getLegacySelectionItems(propz.get(message, ['subscribers', 'lists', 'staffs'], []));
    const [students, staffs, governors] = await Promise.all([
      this.getLegacyRecipientGroup('students', studentItems),
      this.getLegacyRecipientGroup('staffs', schoolStaffItems),
      this.getLegacyRecipientGroup('governors', schoolStaffItems)
    ]);

    return { students, staffs, governors };
  };

  extractEditableEmailBody = (value: string): string => {
    const html = String(value || '');
    if (!html) return '';

    if (typeof DOMParser !== 'undefined') {
      const documentNode = new DOMParser().parseFromString(html, 'text/html');
      const root = documentNode.querySelector('.ql-email-content') || documentNode.body;
      Array.from(root.children).forEach(child => {
        if (/^Attachments:/i.test((child.textContent || '').trim())) {
          child.remove();
        }
      });
      return root.innerHTML;
    }

    return html
      .replace(HTML_FIRST_PART_PAGE_CODE, '')
      .replace(HTML_LAST_PART_PAGE_CODE, '')
      .replace(/<div>\s*Attachments:[\s\S]*$/i, '');
  };

  loadCopiedAudience = (message: any): void => {
    this.setState({ isInitialLoading: true, isError: false, errorMessage: '' });
    this.buildRecipientSelectionStateForMessage(message)
      .then(recipientSelection => {
        const subscriberTypes = propz.get(message, ['subscribers', 'types'], {});
        const studentCount = getGeneralMessageRecipientSelectionGroupSize(recipientSelection.students);
        const staffCount = getGeneralMessageRecipientSelectionGroupSize(recipientSelection.staffs);
        const governorCount = getGeneralMessageRecipientSelectionGroupSize(recipientSelection.governors);
        const audienceGroups = {
          students: Boolean(propz.get(subscriberTypes, ['students'], false)) && studentCount > 0,
          parents: Boolean(propz.get(subscriberTypes, ['parents'], false)) && studentCount > 0,
          staffs: staffCount > 0,
          governors: governorCount > 0
        };
        const hasActiveAudience =
          audienceGroups.students || audienceGroups.parents || audienceGroups.staffs || audienceGroups.governors;

        this.setState(
          {
            parentsDeliverySettings: propz.get(
              message,
              ['subscribers', 'parentsDeliverySettings'],
              DEFAULT_PARENTS_DELIVERY_SETTINGS
            ),
            isSendStudents: audienceGroups.students,
            isSendParents: audienceGroups.parents,
            isSendStaff: audienceGroups.staffs,
            isSendGovernors: audienceGroups.governors,
            recipientSelection,
            isStudentsSelectionCompleted: studentCount > 0,
            isStaffSelectionCompleted: staffCount > 0,
            isGovernorsSelectionCompleted: governorCount > 0,
            isAllUserRecipientsPreselected: hasActiveAudience,
            allUserAudienceGroups: audienceGroups,
            isInitialLoading: false,
            isError: !hasActiveAudience,
            errorMessage: hasActiveAudience
              ? ''
              : 'The original message no longer has active recipients. Please choose a new audience.'
          },
          () => {
            this.savedDraftSnapshot = this.getDraftSnapshot();
          }
        );
      })
      .catch(error => {
        this.setState(
          {
            isInitialLoading: false,
            isError: true,
            errorMessage: getGeneralMessageRequestError(
              error,
              'The saved audience could not be copied. Please choose recipients again.'
            )
          },
          () => {
            this.savedDraftSnapshot = this.getDraftSnapshot();
          }
        );
      });
  };

  loadDraftForEditing = (messageId: string): void => {
    this.setState({ isInitialLoading: true, isError: false, errorMessage: '' });
    getSchoolGeneralMessageForEditor(this.props.user, messageId)
      .then(async message => {
        if (propz.get(message, ['status']) !== 'DRAFT') {
          throw new Error('Only draft messages can be edited.');
        }

        const recipientSelection = await this.buildRecipientSelectionStateForMessage(message);
        const subscriberTypes = propz.get(message, ['subscribers', 'types'], {});
        const savedChannels = propz.get(message, ['deliveryChannels']);
        const smsText = propz.get(message, ['content', 'sms', 'text'], '');
        const emailSubject = propz.get(message, ['content', 'email', 'subject'], '');
        const emailBody = this.extractEditableEmailBody(propz.get(message, ['content', 'email', 'body'], ''));
        const attachments = propz.get(message, ['content', 'email', 'attachments'], []);
        const pushTitle = propz.get(message, ['content', 'push', 'title'], '');
        const pushBody = propz.get(message, ['content', 'push', 'body'], '');
        const staffCount = getGeneralMessageRecipientSelectionGroupSize(recipientSelection.staffs);
        const governorCount = getGeneralMessageRecipientSelectionGroupSize(recipientSelection.governors);
        const isSmsEditorEnabled =
          typeof propz.get(savedChannels, ['sms']) === 'boolean'
            ? propz.get(savedChannels, ['sms'])
            : !TextUtils.isEmpty(smsText);
        const isEmailEditorEnabled =
          typeof propz.get(savedChannels, ['email']) === 'boolean'
            ? propz.get(savedChannels, ['email'])
            : !TextUtils.isEmpty(emailSubject) || !TextUtils.isEmpty(emailBody) || attachments.length > 0;
        const isPushEditorEnabled =
          typeof propz.get(savedChannels, ['push']) === 'boolean'
            ? propz.get(savedChannels, ['push'])
            : !TextUtils.isEmpty(pushTitle) || !TextUtils.isEmpty(pushBody);

        this.setState(
          {
            messageId,
            messageTitle: propz.get(message, ['title'], ''),
            messageDescription: propz.get(message, ['description'], ''),
            parentsDeliverySettings: propz.get(
              message,
              ['subscribers', 'parentsDeliverySettings'],
              DEFAULT_PARENTS_DELIVERY_SETTINGS
            ),
            isSendStudents: Boolean(propz.get(subscriberTypes, ['students'], false)),
            isSendParents: Boolean(propz.get(subscriberTypes, ['parents'], false)),
            isSendStaff: staffCount > 0,
            isSendGovernors: governorCount > 0,
            messageSmsText: isSmsEditorEnabled ? smsText : '',
            messageEmailSubject: isEmailEditorEnabled ? emailSubject : '',
            messageEmailBody: isEmailEditorEnabled ? emailBody : '',
            messageAttachments: isEmailEditorEnabled && Array.isArray(attachments) ? attachments : [],
            messagePushTitle: isPushEditorEnabled ? pushTitle : '',
            messagePushBody: isPushEditorEnabled ? pushBody : '',
            recipientSelection,
            isStudentsSelectionCompleted: getGeneralMessageRecipientSelectionGroupSize(recipientSelection.students) > 0,
            isStaffSelectionCompleted: staffCount > 0,
            isGovernorsSelectionCompleted: governorCount > 0,
            isSmsEditorEnabled,
            isEmailEditorEnabled,
            isPushEditorEnabled,
            senderDisplayName: propz.get(message, ['senderDisplayName'], ''),
            messageScheduleAt: propz.get(message, ['scheduleAt'], '') || '',
            loadedUpdatedAt: propz.get(message, ['updatedAt']),
            lastSavedAt: propz.get(message, ['updatedAt']),
            isInitialLoading: false,
            isError: false,
            errorMessage: ''
          },
          () => {
            this.savedDraftSnapshot = this.getDraftSnapshot();
          }
        );
      })
      .catch(error => {
        this.setState({
          isInitialLoading: false,
          isError: true,
          errorMessage: getGeneralMessageRequestError(error, 'The draft could not be loaded.')
        });
      });
  };

  onNextClick = () => {
    const { isStepChanging } = this.state;

    if (isStepChanging) {
      return;
    }
    const {
      isSmsEditorEnabled,
      isPushEditorEnabled,
      isEmailEditorEnabled,
      messageEmailBody,
      messageEmailSubject,
      messagePushBody,
      messagePushTitle,
      messageSmsText
    } = this.state;

    let newMessageEmailBody;
    if (messageEmailBody === '<p><br></p>') {
      //  because   "<p><br></p>" === ''  in editor
      newMessageEmailBody = '';
      this.setState({
        messageEmailBody: newMessageEmailBody
      });
    } else {
      newMessageEmailBody = messageEmailBody;
    }

    const steps = this.getSteps();
    const normalizedCurrentStep = this.getCurrentStepIndex();
    const currentStepName = steps[normalizedCurrentStep];
    const isCurrentStepContent = currentStepName === MESSAGE_CREATING_STEPS.CONTENT;

    if (!isCurrentStepContent) {
      if (this.isGeneralMessagesV2Enabled()) {
        const nextStepIndex = Math.min(normalizedCurrentStep + 1, steps.length - 1);
        const nextStepName = steps[nextStepIndex];
        this.setState(
          {
            currentStep: nextStepIndex
          },
          () => {
            if (nextStepName === MESSAGE_CREATING_STEPS.AFFECTED_USERS) {
              this.resolveAffectedRecipientsCount();
            }
          }
        );
      } else {
        this.setState({ isStepChanging: true });
        this.saveGeneralMessage()
          .then(message => {
            this.setState(prevState => ({
              currentStep: Math.min(prevState.currentStep + 1, this.getSteps().length - 1),
              messageId: message.id,
              affectedUserList: message.affectedUserList || [],
              affectedUsersCount: (message.affectedUserList || []).length,
              isStepChanging: false
            }));

            this.persistDraftIdInLocation(message.id);
          })
          .catch(error => {
            this.setState({
              errorMessage: getGeneralMessageRequestError(error, 'Error saving general message'),
              isError: true,
              isStepChanging: false
            });
          });
      }
      return;
    }

    // returns an error if content is empty
    const isSMSTextExist = messageSmsText !== '';
    const isPushBodyExist = messagePushBody !== '';
    const isPushTitleExist = messagePushTitle !== '';
    const isEmailSubjectExist = messageEmailSubject !== '';
    const isEmailBodyExist = newMessageEmailBody !== '';

    const isShowSMSError = !isSMSTextExist && isSmsEditorEnabled;
    const isShowPushError = (!isPushBodyExist || !isPushTitleExist) && isPushEditorEnabled;
    const isShowEmailError = (!isEmailSubjectExist || !isEmailBodyExist) && isEmailEditorEnabled;

    if (isCurrentStepContent && (isShowSMSError || isShowPushError || isShowEmailError)) {
      const missingChannels = [
        isShowEmailError ? 'Email subject and body' : '',
        isShowPushError ? 'Push title and body' : '',
        isShowSMSError ? 'SMS text' : ''
      ].filter(Boolean);
      const errorText = `Complete the enabled channels before continuing: ${missingChannels.join(
        '; '
      )}. Add the missing content or turn off channels you do not want to send.`;
      this.setState({
        isError: true,
        errorMessage: errorText
      });
    } else {
      this.setState({
        affectedUserList: [],
        affectedUserChannelSummary: undefined,
        isStepChanging: true,
        isError: false,
        errorMessage: ''
      });

      this.saveGeneralMessage()
        .then(message => {
          const isV2 = this.isGeneralMessagesV2Enabled();
          const classicAffectedUserList = message.affectedUserList || [];
          const affectedUsersCount = propz.get(message, ['affectedUsersCount'], classicAffectedUserList.length);
          this.setState(prevState => ({
            currentStep: Math.min(prevState.currentStep + 1, this.getSteps().length - 1),
            messageId: message.id,
            affectedUserList: isV2 ? [] : classicAffectedUserList,
            affectedUsersCount: isV2
              ? typeof affectedUsersCount === 'number'
                ? affectedUsersCount
                : prevState.affectedUsersCount
              : classicAffectedUserList.length,
            isStepChanging: false
          }));

          //clear search
          this.persistDraftIdInLocation(message.id);
        })
        .catch(error => {
          console.error(error);
          this.setState({
            errorMessage: getGeneralMessageRequestError(error, 'Error saving general message'),
            isError: true,
            isResolvingRecipients: false,
            isStepChanging: false
          });
        });
    }
  };

  onIsEmailEditorChange = event => {
    this.setEmailChannelEnabled(event.target.checked);
  };

  onInsertVariableToSubject = (value: string): void => {
    if (this.subjectInputRef.current) {
      this.subjectInputRef.current.insertVariable(value);
    } else {
      this.setState(prevState => ({ messageEmailSubject: prevState.messageEmailSubject + value }));
    }
  };

  onInsertVariableToFrom = (value: string): void => {
    if (this.fromInputRef.current) {
      this.fromInputRef.current.insertVariable(value);
    } else {
      this.setState(prevState => ({ senderDisplayName: (prevState.senderDisplayName || '') + value }));
    }
  };

  onInsertVariableToPushTitle = (value: string): void => {
    const input = this.pushTitleInputRef.current;
    if (!input) {
      this.setState(prevState => ({ messagePushTitle: prevState.messagePushTitle + value }));
      return;
    }
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const next = this.state.messagePushTitle.slice(0, start) + value + this.state.messagePushTitle.slice(end);
    this.setState({ messagePushTitle: next }, () => {
      const pos = start + value.length;
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  };

  onInsertVariableToPushBody = (value: string): void => {
    const input = this.pushBodyInputRef.current;
    if (!input) {
      this.setState(prevState => ({ messagePushBody: prevState.messagePushBody + value }));
      return;
    }
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const next = this.state.messagePushBody.slice(0, start) + value + this.state.messagePushBody.slice(end);
    this.setState({ messagePushBody: next }, () => {
      const pos = start + value.length;
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  };

  onInsertVariableToSms = (value: string): void => {
    const input = this.smsInputRef.current;
    if (!input) {
      this.setState(prevState => ({ messageSmsText: prevState.messageSmsText + value }));
      return;
    }
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const next = this.state.messageSmsText.slice(0, start) + value + this.state.messageSmsText.slice(end);
    this.setState({ messageSmsText: next }, () => {
      const pos = start + value.length;
      input.focus();
      input.setSelectionRange(pos, pos);
    });
  };

  onIsSmsEditorChange = event => {
    this.setSmsChannelEnabled(event.target.checked);
  };

  onIsPushEditorChange = event => {
    this.setPushChannelEnabled(event.target.checked);
  };

  onPrevClick = () => {
    this.setState(prevState => ({
      currentStep: Math.max(prevState.currentStep - 1, 0)
    }));
  };

  onSaveClick = () => {
    if (this.state.isActionInProgress || this.state.isLoadingFile) {
      return;
    }
    this.setState({ isActionInProgress: true, isError: false, errorMessage: '' });
    this.saveGeneralMessage()
      .then(response => {
        this.setState({
          isActionInProgress: false,
          isError: false,
          errorMessage: '',
          lastSavedAt: propz.get(response, ['updatedAt'], new Date().toISOString())
        });
      })
      .catch(reason => {
        console.error(reason);
        this.setState({ isActionInProgress: false });
        this.onShowError(getGeneralMessageRequestError(reason, 'Error saving general message'));
      });
  };

  onSendClick = () => {
    if (this.state.isActionInProgress) {
      return;
    }

    const { user } = this.props;
    this.setState({ isActionInProgress: true });

    let savedMessage;
    this.saveGeneralMessage()
      .then(message => {
        savedMessage = message;
        return sendSchoolGeneralMessage(user, message.id);
      })
      .then(response => {
        this.onMessageSaved(response || savedMessage);
      })
      .catch(reason => {
        console.error(reason);
        this.setState({ isActionInProgress: false });
        this.onShowError(getGeneralMessageRequestError(reason, 'Error sending message'));
      });
  };

  onScheduleClick = () => {
    if (this.state.isActionInProgress) {
      return;
    }
    if (!this.isScheduleDateValid()) {
      return;
    }
    const { user } = this.props;
    const { messageScheduleAt } = this.state;
    let newMessage;
    this.setState({ isActionInProgress: true, isError: false, errorMessage: '' });

    this.saveGeneralMessage()
      .then(message => {
        newMessage = message;
        return scheduleSchoolGeneralMessage(user, message.id, messageScheduleAt);
      })
      .then(scheduleResponse => {
        // there is message with status DRAFT and we need to reload message from server with new status
        return getSchoolGeneralMessageForEditor(user, newMessage.id).catch(() => scheduleResponse || newMessage);
      })
      .then(message => {
        this.onMessageSaved(message || newMessage);
      })
      .catch(reason => {
        console.error(reason);
        this.setState({ isActionInProgress: false });
        this.onShowError(getGeneralMessageRequestError(reason, 'Error scheduling message'));
      });
  };

  onActiveActionChange = (actionType: ACTION_TYPE) => {
    const isScheduleFormVisible = actionType === ACTION_TYPE.SCHEDULE;
    if (isScheduleFormVisible) {
      this.setState({ isScheduleFormVisible: true });
    } else {
      this.setState({ isScheduleFormVisible: false, messageScheduleAt: '', isError: false, errorMessage: '' });
    }
  };

  onSenderDisplayNameChange = (event): void => {
    this.setState({
      senderDisplayName: event.target.value
    });
  };

  onCancelClick = () => {
    this.goToMessagesList();
  };

  onScheduleDateChange = (event: any) => {
    this.setState({
      messageScheduleAt: event.target.value || '',
      isError: false,
      errorMessage: ''
    });
  };

  onMessageSaved = (message): void => {
    this.goToMessagesList(message);
  };

  onTitleChange = (event): void => {
    this.setState({
      messageTitle: event.target.value
    });
  };

  onDescriptionChange = (event): void => {
    this.setState({
      messageDescription: event.target.value
    });
  };

  onSendParentsChange = (event): void => {
    if (this.state.isAllUserRecipientsPreselected) {
      return;
    }
    this.setState({
      isSendParents: event.target.checked
    });
  };

  onSendStudentsChange = (event): void => {
    if (this.state.isAllUserRecipientsPreselected) {
      return;
    }
    this.setState({
      isSendStudents: event.target.checked
    });
  };

  onSendGovernorsChange = (event): void => {
    if (this.state.isAllUserRecipientsPreselected) {
      return;
    }
    this.setState({
      isSendGovernors: event.target.checked
    });
  };

  onSendStaffChange = (event): void => {
    if (this.state.isAllUserRecipientsPreselected) {
      return;
    }
    this.setState({
      isSendStaff: event.target.checked
    });
  };

  onDeliverySettingsChanged = (settings: ParentsDeliverySettings): void => {
    if (this.state.isAllUserRecipientsPreselected) {
      return;
    }
    this.setState({
      parentsDeliverySettings: settings
    });
  };

  onEmailSubjectChange = event => {
    this.setState({
      messageEmailSubject: event.target.value
    });
  };

  onEmailBodyChange = body => {
    this.setState({
      messageEmailBody: body
    });
  };

  onSmsTextChange = event => {
    this.setState({
      messageSmsText: event.target.value
    });
  };

  onPushTitleChange = event => {
    this.setState({
      messagePushTitle: event.target.value
    });
  };

  onPushBodyChange = event => {
    this.setState({
      messagePushBody: event.target.value
    });
  };

  onEmailAttachmentChange = async event => {
    const { user } = this.props;
    const { maxSizeUploadFile, messageAttachments } = this.state;
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (!file) return;

    input.value = '';
    const currentFileSize = file.size;
    const fileName = file.name;
    const fileNameWithoutSpaces = fileName.split(' ').join('_');
    const validation = validateGeneralMessageAttachmentAddition(messageAttachments, currentFileSize, maxSizeUploadFile);

    if (!validation.isValid) {
      this.setState({
        errorMessage: validation.errorMessage || 'This file cannot be attached.',
        isError: true
      });
      return;
    }

    this.setState({ isLoadingFile: true });

    let contentHash: string;
    try {
      contentHash = await getGeneralMessageAttachmentContentHash(file);
    } catch (error) {
      this.setState({
        errorMessage: 'This attachment could not be read. Choose the file again and retry.',
        isError: true,
        isLoadingFile: false
      });
      return;
    }

    const candidateAttachment: Attachment = {
      fileName: fileNameWithoutSpaces,
      externalFileId: '',
      fileSize: currentFileSize,
      contentHash
    };
    if (
      isGeneralMessageAttachmentDuplicate(this.state.messageAttachments, candidateAttachment) ||
      this.pendingAttachmentContentHashes.has(contentHash)
    ) {
      this.setState({
        errorMessage: getGeneralMessageDuplicateAttachmentError(fileName),
        isError: true,
        isLoadingFile: false
      });
      return;
    }

    this.pendingAttachmentContentHashes.add(contentHash);

    const formData = new FormData();
    formData.append('file', file, fileNameWithoutSpaces);

    uploadFileAllPath(user, formData)
      .then(data => {
        const { key, link } = data;
        this.setState(state => ({
          messageAttachments: [
            ...state.messageAttachments,
            {
              fileName: fileNameWithoutSpaces,
              link: link,
              externalFileId: key,
              fileSize: currentFileSize,
              contentHash
            }
          ],
          isLoadingFile: false
        }));
        this.pendingAttachmentContentHashes.delete(contentHash);
      })
      .catch(error => {
        this.pendingAttachmentContentHashes.delete(contentHash);
        this.setState({
          errorMessage: getGeneralMessageAttachmentUploadError(error, maxSizeUploadFile),
          isError: true,
          isLoadingFile: false
        });
      });
  };

  onRemoveAttachment = (key: string) => {
    const attachments = [...this.state.messageAttachments];
    const attachmentIndex = attachments.findIndex(attachment => attachment.externalFileId === key);

    if (attachmentIndex < 0) return;

    attachments.splice(attachmentIndex, 1);
    this.setState({ messageAttachments: attachments });
  };

  onSelectedStudentsChange = (students: SchoolStudent[]) => {
    this.setState({
      selectedStudents: students
    });
  };

  onStudentsSelectionCompleted = (isCompleted: boolean) => {
    this.setState({
      isStudentsSelectionCompleted: isCompleted
    });
  };

  onSelectedStaffChange = (staff: SchoolUser[]) => {
    this.setState({
      selectedStaff: staff
    });
  };

  onStaffSelectionCompleted = (isCompleted: boolean) => {
    this.setState({
      isStaffSelectionCompleted: isCompleted
    });
  };

  onSelectedGovernorsChange = (governors: SchoolUser[]) => {
    this.setState({
      selectedGovernors: governors
    });
  };

  onGovernorsSelectionCompleted = (isCompleted: boolean) => {
    this.setState({
      isGovernorsSelectionCompleted: isCompleted
    });
  };

  getRecipientSelectionItemsFromSchoolUsers = (items: SchoolUser[]): GeneralMessageRecipientSelectionItem[] => {
    return items
      .map(item => ({
        userId: item.id,
        permissionId: propz.get(item, ['permissions', 0, 'id'])
      }))
      .filter(({ userId, permissionId }) => !TextUtils.isEmpty(userId) && !TextUtils.isEmpty(permissionId));
  };

  getRecipientSelectionItemsFromStudents = (items: SchoolStudent[]): GeneralMessageRecipientSelectionItem[] => {
    return items
      .map(item => ({
        userId: item.id,
        permissionId: item.permissionId
      }))
      .filter(({ userId, permissionId }) => !TextUtils.isEmpty(userId) && !TextUtils.isEmpty(permissionId));
  };

  getRecipientSelectionForSave = (): GeneralMessageRecipientSelection => {
    const { recipientSelection, selectedStudents, selectedStaff, selectedGovernors } = this.state;

    const selectionState: GeneralMessageRecipientSelectionState = {
      ...recipientSelection,
      students: {
        ...recipientSelection.students,
        selected:
          recipientSelection.students.selected.length > 0 || recipientSelection.students.mode === 'FILTER'
            ? recipientSelection.students.selected
            : this.getRecipientSelectionItemsFromStudents(selectedStudents)
      },
      staffs: {
        ...recipientSelection.staffs,
        selected:
          recipientSelection.staffs.selected.length > 0 || recipientSelection.staffs.mode === 'FILTER'
            ? recipientSelection.staffs.selected
            : this.getRecipientSelectionItemsFromSchoolUsers(selectedStaff)
      },
      governors: {
        ...recipientSelection.governors,
        selected:
          recipientSelection.governors.selected.length > 0 || recipientSelection.governors.mode === 'FILTER'
            ? recipientSelection.governors.selected
            : this.getRecipientSelectionItemsFromSchoolUsers(selectedGovernors)
      }
    };

    return getGeneralMessageRecipientSelectionForSave(selectionState);
  };

  getSubscribersForSave = () => {
    const {
      isSendParents,
      isSendStudents,
      isSendStaff,
      isSendGovernors,
      parentsDeliverySettings,
      selectedStudents,
      selectedStaff,
      selectedGovernors
    } = this.state;

    const staffs = selectedStaff
      .map(staff => {
        return {
          userId: staff.id,
          permissionId: propz.get(staff, ['permissions', 0, 'id'])
        };
      })
      .filter(({ userId, permissionId }) => !TextUtils.isEmpty(userId) && !TextUtils.isEmpty(permissionId));

    const governors = selectedGovernors
      .map(governor => {
        return {
          userId: governor.id,
          permissionId: propz.get(governor, ['permissions', 0, 'id'])
        };
      })
      .filter(({ userId, permissionId }) => !TextUtils.isEmpty(userId) && !TextUtils.isEmpty(permissionId));

    const students = selectedStudents
      .map(student => {
        return {
          userId: student.id,
          permissionId: student.permissionId
        };
      })
      .filter(({ userId, permissionId }) => !TextUtils.isEmpty(userId) && !TextUtils.isEmpty(permissionId));

    return {
      types: {
        students: isSendStudents,
        parents: isSendParents,
        schoolStaff: isSendStaff || isSendGovernors
      },
      lists: {
        students: students,
        staffs: [...staffs, ...governors]
      },
      parentsDeliverySettings: parentsDeliverySettings
    };
  };

  getEnabledChannelsForRecipientResolve = () => ({
    email: this.state.isEmailEditorEnabled,
    push: this.state.isPushEditorEnabled,
    sms: this.state.isSmsEditorEnabled
  });

  hasChildVariablesInMessage = (): boolean => {
    const childVariables = [
      '{{childFirstName}}',
      '{{childLastName}}',
      '{{childFullName}}',
      '{{childForm}}',
      '{{childYearGroup}}',
      '{{childHouse}}'
    ];
    const messageContent = [
      this.state.messageEmailSubject,
      this.state.messageEmailBody,
      this.state.messagePushTitle,
      this.state.messagePushBody,
      this.state.messageSmsText
    ].join(' ');

    return childVariables.some(variable => messageContent.indexOf(variable) !== -1);
  };

  getChildVariableAudienceWarning = (): string => {
    if (!this.hasChildVariablesInMessage() || (!this.state.isSendStaff && !this.state.isSendGovernors)) return '';
    return 'This message includes child variables. They will be personalised for students and linked parents; in copies sent to school staff or trustees/governors, the child variable tags will remain unchanged.';
  };

  resolveAffectedRecipientsCount = (): Promise<number> => {
    const { user } = this.props;

    this.setState({
      isResolvingRecipients: true,
      affectedUserList: [],
      affectedUserChannelSummary: undefined
    });

    return resolveGeneralMessageRecipients(user, {
      recipientSelection: this.getRecipientSelectionForSave(),
      subscribers: this.getSubscribersForSave(),
      includeList: false,
      enabledChannels: this.getEnabledChannelsForRecipientResolve(),
      hasChildVariables: this.hasChildVariablesInMessage()
    })
      .then(result => {
        const count = propz.get(result, ['count'], 0);
        const channelSummary = propz.get(result, ['channelSummary']);

        this.setState({
          affectedUsersCount: count,
          affectedUserList: [],
          affectedUserChannelSummary: channelSummary,
          isResolvingRecipients: false
        });

        return count;
      })
      .catch(error => {
        this.setState({ isResolvingRecipients: false });
        throw error;
      });
  };

  loadAffectedRecipientsList = (): void => {
    const { user } = this.props;

    this.setState({
      isLoadingAffectedUserList: true,
      isError: false,
      errorMessage: ''
    });

    resolveGeneralMessageRecipients(user, {
      recipientSelection: this.getRecipientSelectionForSave(),
      subscribers: this.getSubscribersForSave(),
      includeList: true,
      enabledChannels: this.getEnabledChannelsForRecipientResolve(),
      hasChildVariables: this.hasChildVariablesInMessage()
    })
      .then(result => {
        const count = propz.get(result, ['count'], 0);
        const affectedUserList = propz.get(result, ['affectedUserList'], []);
        const channelSummary = propz.get(result, ['channelSummary']);

        this.setState({
          affectedUsersCount: count,
          affectedUserList,
          affectedUserChannelSummary: channelSummary,
          isLoadingAffectedUserList: false
        });
      })
      .catch(error => {
        console.error(error);

        this.setState({
          errorMessage: getGeneralMessageRequestError(error, 'Recipients could not be loaded'),
          isError: true,
          isLoadingAffectedUserList: false
        });
      });
  };

  onRecipientSelectionGroupChange = (
    recipientType: GeneralMessageRecipientType,
    group: GeneralMessageRecipientSelectionStateGroup
  ): void => {
    this.setState(prevState => ({
      recipientSelection: updateGeneralMessageRecipientSelectionStateGroup(
        prevState.recipientSelection,
        recipientType,
        group
      )
    }));
  };

  onRecipientSelectionCompleted = (recipientType: GeneralMessageRecipientType, isCompleted: boolean): void => {
    switch (recipientType) {
      case 'students':
        this.onStudentsSelectionCompleted(isCompleted);
        break;
      case 'staffs':
        this.onStaffSelectionCompleted(isCompleted);
        break;
      case 'governors':
        this.onGovernorsSelectionCompleted(isCompleted);
        break;
    }
  };

  onCloseErrorClick = event => {
    event.preventDefault();

    this.setState({
      isError: false,
      errorMessage: ''
    });
  };

  onCloseImageError = () => {
    this.setState({
      isImageError: false
    });
  };

  onShowError = (message: string) => {
    const errorMessage = typeof message === 'string' ? message : 'Error saving general message';

    this.setState({
      isError: true,
      errorMessage: errorMessage
    });
  };

  isScheduleDateValid = (): boolean => {
    const { messageScheduleAt } = this.state;

    if (TextUtils.isEmpty(messageScheduleAt)) {
      this.onShowError('Please enter a valid date');
      return false;
    }

    const scheduleDate = new Date(messageScheduleAt);
    const scheduleTime = scheduleDate.getTime();

    if (Number.isNaN(scheduleTime)) {
      this.onShowError('Please enter a valid date');
      return false;
    }

    if (Date.now() > scheduleTime) {
      this.onShowError('The scheduled sending time is in the past, please amend it');
      return false;
    }

    return true;
  };

  isGeneralMessagesV2Enabled(): boolean {
    return true;
  }

  getSteps(): MESSAGE_CREATING_STEPS[] {
    const {
      isSendStudents,
      isSendParents,
      isSendStaff,
      isSendGovernors,
      isStudentRecipientsPreselected,
      isAllUserRecipientsPreselected
    } = this.state;
    let steps = [MESSAGE_CREATING_STEPS.SETTINGS];

    if ((isSendParents || isSendStudents) && !isStudentRecipientsPreselected && !isAllUserRecipientsPreselected) {
      steps.push(MESSAGE_CREATING_STEPS.STUDENT_LIST);
    }
    if (isSendStaff && !isAllUserRecipientsPreselected) {
      steps.push(MESSAGE_CREATING_STEPS.STAFF_LIST);
    }
    if (isSendGovernors && !isAllUserRecipientsPreselected) {
      steps.push(MESSAGE_CREATING_STEPS.GOVERNORS_LIST);
    }

    steps.push(MESSAGE_CREATING_STEPS.CONTENT);
    steps.push(MESSAGE_CREATING_STEPS.PREVIEW);
    steps.push(MESSAGE_CREATING_STEPS.AFFECTED_USERS);

    return steps;
  }

  getCurrentStepIndex(): number {
    const lastStepIndex = Math.max(this.getSteps().length - 1, 0);
    return Math.min(Math.max(this.state.currentStep, 0), lastStepIndex);
  }

  getStepTitleByName(stepName: MESSAGE_CREATING_STEPS): string {
    switch (stepName) {
      case MESSAGE_CREATING_STEPS.SETTINGS:
        return 'Settings';

      case MESSAGE_CREATING_STEPS.STUDENT_LIST:
        return 'Students';

      case MESSAGE_CREATING_STEPS.STAFF_LIST:
        return 'Staff';

      case MESSAGE_CREATING_STEPS.GOVERNORS_LIST:
        return 'Governors';

      case MESSAGE_CREATING_STEPS.CONTENT:
        return 'Compose';

      case MESSAGE_CREATING_STEPS.PREVIEW:
        return 'Preview';

      case MESSAGE_CREATING_STEPS.AFFECTED_USERS:
        return 'Recipients';
    }
  }

  getStepTitle(): string {
    const currentStep = this.getCurrentStepIndex();
    const steps = this.getSteps();
    return this.getStepTitleByName(steps[currentStep]);
  }

  private getPageHelpSignature(): string {
    const step = this.getSteps()[this.getCurrentStepIndex()];
    return JSON.stringify({
      step,
      isEditMode: this.state.isEditMode,
      title: this.state.messageTitle,
      audience: this.getHeaderAudienceLabels(),
      channels: [this.state.isEmailEditorEnabled, this.state.isPushEditorEnabled, this.state.isSmsEditorEnabled],
      studentCount: getGeneralMessageRecipientSelectionGroupSize(this.state.recipientSelection.students),
      staffCount: getGeneralMessageRecipientSelectionGroupSize(this.state.recipientSelection.staffs),
      governorCount: getGeneralMessageRecipientSelectionGroupSize(this.state.recipientSelection.governors),
      affectedUsersCount: this.state.affectedUsersCount,
      attachments: this.state.messageAttachments.length,
      scheduleAt: this.state.messageScheduleAt,
      isError: this.state.isError,
      isInitialLoading: this.state.isInitialLoading
    });
  }

  private updatePageHelpDefinition(): void {
    if (!this.pageHelpRegistration) return;
    const signature = this.getPageHelpSignature();
    if (signature === this.pageHelpSignature) return;
    this.pageHelpSignature = signature;
    this.pageHelpRegistration.update(this.getPageHelpDefinition());
  }

  private getPageHelpDefinition(): PageHelpDefinition {
    const step = this.getSteps()[this.getCurrentStepIndex()];
    const stepTitle = this.getStepTitleByName(step);
    const mode = this.state.isEditMode ? 'edit' : 'create';
    const audience = this.getHeaderAudienceLabels();
    const enabledChannels = [
      this.state.isEmailEditorEnabled ? 'Email' : '',
      this.state.isPushEditorEnabled ? 'Push' : '',
      this.state.isSmsEditorEnabled ? 'SMS' : ''
    ].filter(Boolean);
    const recipientGroup =
      step === MESSAGE_CREATING_STEPS.STUDENT_LIST
        ? this.state.recipientSelection.students
        : step === MESSAGE_CREATING_STEPS.STAFF_LIST
        ? this.state.recipientSelection.staffs
        : this.state.recipientSelection.governors;
    const recipientCount = getGeneralMessageRecipientSelectionGroupSize(recipientGroup);
    const screenSuffix =
      step === MESSAGE_CREATING_STEPS.STUDENT_LIST
        ? 'recipients.students'
        : step === MESSAGE_CREATING_STEPS.STAFF_LIST
        ? 'recipients.staff'
        : step === MESSAGE_CREATING_STEPS.GOVERNORS_LIST
        ? 'recipients.governors'
        : step.toLowerCase().replace('_', '-');
    const isRecipientStep = [
      MESSAGE_CREATING_STEPS.STUDENT_LIST,
      MESSAGE_CREATING_STEPS.STAFF_LIST,
      MESSAGE_CREATING_STEPS.GOVERNORS_LIST
    ].includes(step);
    const observations: any[] = [];

    if (this.state.isInitialLoading) {
      observations.push({
        id: 'loading-message',
        tone: 'neutral',
        title: 'Loading the message',
        text: 'Squady is waiting for the saved draft before checking this step.'
      });
    } else if (step === MESSAGE_CREATING_STEPS.SETTINGS) {
      observations.push({
        id: 'audience-progress',
        tone: audience.length > 0 ? 'positive' : 'warning',
        title: audience.length > 0 ? `Audience: ${audience.join(', ')}` : 'Choose at least one audience',
        text:
          audience.length > 0
            ? 'Only the recipient-selection steps needed for these groups will be added to the wizard.'
            : 'Select students, parents, staff or trustees/governors before continuing.'
      });
    } else if (isRecipientStep) {
      observations.push({
        id: 'recipient-selection',
        tone: recipientCount > 0 ? 'positive' : 'warning',
        title: `${recipientCount.toLocaleString('en-GB')} ${
          recipientCount === 1 ? 'recipient' : 'recipients'
        } selected`,
        text:
          'The left panel contains available people. The right panel is the staged selection that will continue to the message.'
      });
    } else if (step === MESSAGE_CREATING_STEPS.CONTENT) {
      observations.push({
        id: 'enabled-channels',
        tone: enabledChannels.length > 0 ? 'positive' : 'warning',
        title: enabledChannels.length > 0 ? `Enabled: ${enabledChannels.join(', ')}` : 'No delivery channel enabled',
        text:
          'Each enabled channel needs its own required content. If Next finds something missing, the relevant editor and error summary will be brought into view.'
      });
      if (this.state.messageAttachments.length > 0) {
        observations.push({
          id: 'attachments',
          tone: 'neutral',
          title: `${this.state.messageAttachments.length} attachment${
            this.state.messageAttachments.length === 1 ? '' : 's'
          }`,
          text: 'The attachment limit applies to the combined size of all files in this message.'
        });
      }
    } else if (step === MESSAGE_CREATING_STEPS.PREVIEW) {
      observations.push({
        id: 'preview-channels',
        tone: 'neutral',
        title: `Previewing ${enabledChannels.join(', ') || 'message content'}`,
        text: 'Switch between the enabled channel previews and check the content before resolving recipients.'
      });
    } else if (step === MESSAGE_CREATING_STEPS.AFFECTED_USERS) {
      observations.push({
        id: 'resolved-recipients',
        tone: this.state.affectedUsersCount > 0 ? 'positive' : 'warning',
        title: `${this.state.affectedUsersCount.toLocaleString('en-GB')} users affected`,
        text:
          'These are unique users resolved from the chosen roles and priorities. Load the detailed list only when you need to inspect individual recipients.'
      });
    }

    if (this.state.isError) {
      observations.push({
        id: 'message-error',
        tone: 'danger',
        title: 'This step needs attention',
        text: this.state.errorMessage || 'Review the highlighted fields before continuing.'
      });
    }

    const sections = isRecipientStep
      ? [
          {
            id: 'recipient-workflow',
            title: 'Build the recipient list',
            summary:
              'Search and filter on the left, select people, then move them to the right. Double-clicking a row moves that person immediately.',
            items: [
              'Selections remain staged while you work on this step.',
              'Use the right-side search to check the selected list.',
              'Clear selected removes the staged recipients from this group; it does not delete users.'
            ]
          }
        ]
      : step === MESSAGE_CREATING_STEPS.CONTENT
      ? [
          {
            id: 'channel-content',
            title: 'Compose each enabled channel',
            summary:
              'Email, Push and SMS are separate messages. Turning a channel off keeps its draft content ready in case you turn it back on before leaving this editor.',
            items: [
              'Attachments belong to Email.',
              'Variables are resolved separately for every recipient.',
              'Use Preview after all enabled channels are complete.'
            ]
          }
        ]
      : step === MESSAGE_CREATING_STEPS.AFFECTED_USERS
      ? [
          {
            id: 'recipient-resolution',
            title: 'Understand the resolved audience',
            summary:
              'A person can qualify through more than one role, but the summary counts unique users. Channel counts only appear for channels enabled in this message.'
          }
        ]
      : [
          {
            id: 'current-step',
            title: stepTitle,
            summary:
              step === MESSAGE_CREATING_STEPS.SETTINGS
                ? 'Give staff a recognisable internal title, choose the audience and set parent contact priorities.'
                : 'Review exactly what recipients will see before you continue.'
          }
        ];

    return {
      screenId: `school.general-messages.${mode}.${screenSuffix}`,
      title: `${this.state.isEditMode ? 'Edit general message' : 'New general message'} · ${stepTitle}`,
      subtitle: `Step ${this.getCurrentStepIndex() + 1} of ${
        this.getSteps().length
      }. Squady follows the choices you make here.`,
      entity: { singular: 'message', plural: 'messages' },
      observations,
      anchors: [
        {
          id: 'gm-create-header',
          label: 'Message summary',
          description: 'The title, audience, channels, attachments and current wizard step update here as you work.'
        },
        {
          id: 'gm-create-step',
          label: `${stepTitle} step`,
          description: isRecipientStep
            ? 'Choose and stage the people who should be included in this audience group.'
            : `Complete the ${stepTitle.toLowerCase()} information in this card.`
        },
        {
          id: 'gm-create-actions',
          label: 'Wizard actions',
          description: 'Move between steps, save a draft, send now or schedule when those actions are available.'
        }
      ],
      actions: [
        ...(this.getCurrentStepIndex() > 0
          ? [
              {
                id: 'previous-step',
                label: 'Go to previous step',
                description: 'Return without discarding the information already entered.',
                run: this.onPrevClick
              }
            ]
          : []),
        ...(this.getCurrentStepIndex() < this.getSteps().length - 1
          ? [
              {
                id: 'next-step',
                label: 'Continue to next step',
                description: 'Validate this step and continue through the message wizard.',
                kind: 'primary' as 'primary',
                disabled: this.isNextButtonDisabled(),
                run: this.onNextClick
              }
            ]
          : []),
        {
          id: 'leave-message',
          label: 'Return to messages',
          description: 'Squad In Touch will warn you first if this message has unsaved changes.',
          run: this.onCancelClick
        }
      ],
      sections
    };
  }
  buildGeneralMessageData = () => {
    const {
      messageTitle,
      messageDescription,
      messageSmsText,
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageAttachments,
      senderDisplayName,
      isSmsEditorEnabled,
      isEmailEditorEnabled,
      isPushEditorEnabled
    } = this.state;

    const cleanedEmailBody = stripVariableChips(messageEmailBody);
    const messageHTMLEmailBody =
      cleanedEmailBody !== '' ? `${HTML_FIRST_PART_PAGE_CODE} ${cleanedEmailBody} ${HTML_LAST_PART_PAGE_CODE}` : '';

    return {
      title: messageTitle,
      description: messageDescription,
      senderDisplayName,
      deliveryChannels: {
        sms: isSmsEditorEnabled,
        email: isEmailEditorEnabled,
        push: isPushEditorEnabled
      },
      content: {
        sms: { text: messageSmsText },
        email: {
          subject: messageEmailSubject,
          body: messageHTMLEmailBody,
          attachments: messageAttachments
        },
        push: { title: messagePushTitle, body: messagePushBody }
      },
      subscribers: this.getSubscribersForSave(),
      ...(this.isGeneralMessagesV2Enabled() ? { recipientSelection: this.getRecipientSelectionForSave() } : {})
    };
  };

  saveGeneralMessage = () => {
    const attachmentValidation = validateGeneralMessageAttachmentCollection(
      this.state.messageAttachments,
      this.state.maxSizeUploadFile
    );

    if (!attachmentValidation.isValid) {
      return Promise.reject(new Error(attachmentValidation.errorMessage || 'The attachments could not be validated.'));
    }

    const { user } = this.props;
    const data: any = this.buildGeneralMessageData();
    const wasNewMessage = typeof this.state.messageId === 'undefined';
    const request = !wasNewMessage
      ? updateSchoolGeneralMessage(
          user,
          {
            ...data,
            ...(this.state.loadedUpdatedAt ? { expectedUpdatedAt: this.state.loadedUpdatedAt } : {})
          },
          this.state.messageId
        )
      : createSchoolGeneralMessage(user, data);

    return request.then(message => {
      return new Promise(resolve => {
        this.setState(
          {
            messageId: message.id,
            isEditMode: true,
            loadedUpdatedAt: propz.get(message, ['updatedAt'], this.state.loadedUpdatedAt),
            lastSavedAt: propz.get(message, ['updatedAt'], new Date().toISOString())
          },
          () => {
            this.savedDraftSnapshot = this.getDraftSnapshot();
            if (wasNewMessage) {
              this.persistDraftIdInLocation(message.id);
            }
            resolve(message);
          }
        );
      });
    });
  };

  goToMessagesList(message?) {
    const { history } = this.props;
    const search = propz.get(history, ['location', 'state', 'search'], '');
    const returnTo = propz.get(history, ['location', 'state', 'returnTo']);

    if (returnTo && returnTo.pathname) {
      history.push({
        pathname: returnTo.pathname,
        search: returnTo.search || '',
        state: typeof message !== 'undefined' ? { message } : undefined
      });
      return;
    }

    if (typeof message !== 'undefined') {
      history.push({
        pathname: `/generalMessages`,
        search,
        state: {
          message
        }
      });
    } else {
      history.push({
        pathname: `/generalMessages`,
        search
      });
    }
  }

  isNextButtonDisabled(): boolean {
    const {
      isSendStaff,
      isSendStudents,
      isSendParents,
      isSendGovernors,
      affectedUserList,
      affectedUsersCount,
      isError,
      isSmsEditorEnabled,
      isEmailEditorEnabled,
      isPushEditorEnabled,
      isLoadingFile,
      isStudentsSelectionCompleted,
      isStaffSelectionCompleted,
      isGovernorsSelectionCompleted,
      isStepChanging,
      isResolvingRecipients,
      isActionInProgress
    } = this.state;
    const steps = this.getSteps();
    const normalizedCurrentStep = this.getCurrentStepIndex();
    const currentStepName = steps[normalizedCurrentStep];

    if (isStepChanging || isResolvingRecipients || isActionInProgress) {
      return true;
    }
    let isNextButtonDisabled = false;

    const isAllEditorDisabled = !isSmsEditorEnabled && !isPushEditorEnabled && !isEmailEditorEnabled;
    switch (true) {
      case currentStepName === MESSAGE_CREATING_STEPS.SETTINGS:
        isNextButtonDisabled = !isSendStaff && !isSendGovernors && !isSendParents && !isSendStudents;
        break;
      case currentStepName === MESSAGE_CREATING_STEPS.STUDENT_LIST:
        isNextButtonDisabled = !isStudentsSelectionCompleted;
        break;
      case currentStepName === MESSAGE_CREATING_STEPS.STAFF_LIST:
        isNextButtonDisabled = !isStaffSelectionCompleted;
        break;
      case currentStepName === MESSAGE_CREATING_STEPS.GOVERNORS_LIST:
        isNextButtonDisabled = !isGovernorsSelectionCompleted;
        break;
      case currentStepName === MESSAGE_CREATING_STEPS.CONTENT:
        // Content validation runs on click so the user receives a precise,
        // visible explanation instead of an inert disabled button.
        isNextButtonDisabled = isLoadingFile || isAllEditorDisabled;
        break;
      case currentStepName === MESSAGE_CREATING_STEPS.AFFECTED_USERS:
        isNextButtonDisabled =
          (typeof affectedUserList === 'undefined' || affectedUserList.length === 0) && affectedUsersCount === 0;
        break;
      case isError:
        isNextButtonDisabled = true;
        break;
    }

    return isNextButtonDisabled;
  }

  renderViewByStep(): React.ReactNode {
    const currentStep = this.getCurrentStepIndex();
    const steps = this.getSteps();
    const currentStepName = steps[currentStep];

    switch (currentStepName) {
      case MESSAGE_CREATING_STEPS.SETTINGS:
        return this.renderMessageSettingsForm();

      case MESSAGE_CREATING_STEPS.STUDENT_LIST:
        return this.renderStudentList();

      case MESSAGE_CREATING_STEPS.STAFF_LIST:
        return this.renderStaffList();

      case MESSAGE_CREATING_STEPS.GOVERNORS_LIST:
        return this.renderGovernorsList();

      case MESSAGE_CREATING_STEPS.CONTENT:
        return this.renderMessageTextForm();

      case MESSAGE_CREATING_STEPS.PREVIEW:
        return this.renderMessagePreview();

      case MESSAGE_CREATING_STEPS.AFFECTED_USERS:
        return this.renderAffectedUsers();
    }
  }

  renderMessageSettingsForm(): React.ReactNode {
    const {
      messageTitle,
      messageDescription,
      isSendParents,
      isSendStudents,
      isSendStaff,
      isSendGovernors,
      parentsDeliverySettings,
      isAllUserRecipientsPreselected,
      allUserAudienceGroups
    } = this.state;

    return (
      <GeneralMessageSettingsForm
        messageTitle={messageTitle}
        messageDescription={messageDescription}
        parentsDeliverySettings={parentsDeliverySettings}
        onTitleChange={this.onTitleChange}
        onDescriptionChange={this.onDescriptionChange}
        isSendParents={isSendParents}
        isSendStudents={isSendStudents}
        isSendStaff={isSendStaff}
        isSendGovernors={isSendGovernors}
        onIsSendParentsChange={this.onSendParentsChange}
        onIsSendStudentsChange={this.onSendStudentsChange}
        onSendStaffChange={this.onSendStaffChange}
        onSendGovernorsChange={this.onSendGovernorsChange}
        onDeliverySettingsChanged={this.onDeliverySettingsChanged}
        isRecipientsReadOnly={isAllUserRecipientsPreselected}
        displaySendStudents={isAllUserRecipientsPreselected ? allUserAudienceGroups.students : undefined}
        displaySendParents={isAllUserRecipientsPreselected ? allUserAudienceGroups.parents : undefined}
        displaySendStaff={isAllUserRecipientsPreselected ? allUserAudienceGroups.staffs : undefined}
        displaySendGovernors={isAllUserRecipientsPreselected ? allUserAudienceGroups.governors : undefined}
        hideParentDeliverySettings={isAllUserRecipientsPreselected}
      />
    );
  }

  renderStudentList(): React.ReactNode {
    if (!this.isGeneralMessagesV2Enabled()) {
      return (
        <CreateGeneralMessageStudentsStep
          user={this.props.user}
          history={this.props.history}
          location={this.props.location}
          onSelectedItemsChange={this.onSelectedStudentsChange}
          selectedItems={this.state.selectedStudents}
          onStudentsSelectionCompleted={this.onStudentsSelectionCompleted}
        />
      );
    }

    return (
      <CreateGeneralMessageRecipientsStepV2
        user={this.props.user}
        recipientType="students"
        selectionGroup={this.state.recipientSelection.students}
        onSelectionGroupChange={this.onRecipientSelectionGroupChange}
        onSelectionCompleted={this.onRecipientSelectionCompleted}
        title="Select students"
        availableTitle="Available students"
        selectedTitle="Selected students"
        hideHeader={true}
      />
    );
  }

  renderStaffList(): React.ReactNode {
    if (!this.isGeneralMessagesV2Enabled()) {
      return (
        <CreateGeneralMessageStaffStep
          user={this.props.user}
          history={this.props.history}
          location={this.props.location}
          selectedItems={this.state.selectedStaff}
          onSelectedItemsChange={this.onSelectedStaffChange}
          onStaffSelectionCompleted={this.onStaffSelectionCompleted}
        />
      );
    }

    return (
      <CreateGeneralMessageRecipientsStepV2
        user={this.props.user}
        recipientType="staffs"
        selectionGroup={this.state.recipientSelection.staffs}
        onSelectionGroupChange={this.onRecipientSelectionGroupChange}
        onSelectionCompleted={this.onRecipientSelectionCompleted}
        title="Select school staff"
        availableTitle="Available staff"
        selectedTitle="Selected staff"
        hideHeader={true}
      />
    );
  }

  renderGovernorsList(): React.ReactNode {
    if (!this.isGeneralMessagesV2Enabled()) {
      return (
        <CreateGeneralMessageGovernorsStep
          user={this.props.user}
          history={this.props.history}
          location={this.props.location}
          selectedItems={this.state.selectedGovernors}
          onSelectedItemsChange={this.onSelectedGovernorsChange}
          onGovernorsSelectionCompleted={this.onGovernorsSelectionCompleted}
        />
      );
    }

    return (
      <CreateGeneralMessageRecipientsStepV2
        user={this.props.user}
        recipientType="governors"
        selectionGroup={this.state.recipientSelection.governors}
        onSelectionGroupChange={this.onRecipientSelectionGroupChange}
        onSelectionCompleted={this.onRecipientSelectionCompleted}
        title="Select trustees/governors"
        availableTitle="Available trustees/governors"
        selectedTitle="Selected trustees/governors"
        hideHeader={true}
      />
    );
  }

  renderMessageTextForm(): React.ReactNode {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageSmsText,
      messageAttachments,
      senderDisplayName,
      isImageError,
      isError,
      isLoadingFile,
      isEmailEditorEnabled,
      isPushEditorEnabled,
      isSmsEditorEnabled
    } = this.state;

    if (this.isGeneralMessagesV2Enabled()) {
      return (
        <GeneralMessageContentV2
          user={this.props.user}
          isEmailEnabled={isEmailEditorEnabled}
          isPushEnabled={isPushEditorEnabled}
          isSmsEnabled={isSmsEditorEnabled}
          onEmailEnabledChange={this.setEmailChannelEnabled}
          onPushEnabledChange={this.setPushChannelEnabled}
          onSmsEnabledChange={this.setSmsChannelEnabled}
          messageEmailSubject={messageEmailSubject}
          messageEmailBody={messageEmailBody}
          messagePushTitle={messagePushTitle}
          messagePushBody={messagePushBody}
          messageSmsText={messageSmsText}
          messageAttachments={messageAttachments}
          senderDisplayName={senderDisplayName}
          onEmailSubjectChange={this.onEmailSubjectChange}
          onEmailBodyChange={this.onEmailBodyChange}
          onPushTitleChange={this.onPushTitleChange}
          onPushBodyChange={this.onPushBodyChange}
          onSmsTextChange={this.onSmsTextChange}
          onSenderDisplayNameChange={this.onSenderDisplayNameChange}
          onEmailAttachmentChange={this.onEmailAttachmentChange}
          onRemoveAttachment={this.onRemoveAttachment}
          onCloseImageError={this.onCloseImageError}
          showStudentVariables={this.state.isSendStudents || this.state.isSendParents}
          showParentVariables={this.state.isSendParents}
          isError={isError}
          isImageError={isImageError}
          isLoadingFile={isLoadingFile}
          editorRef={this.editorRef}
          subjectInputRef={this.subjectInputRef}
          fromInputRef={this.fromInputRef}
          pushTitleInputRef={this.pushTitleInputRef}
          pushBodyInputRef={this.pushBodyInputRef}
          smsInputRef={this.smsInputRef}
          onInsertVariableToSubject={this.onInsertVariableToSubject}
          onInsertVariableToFrom={this.onInsertVariableToFrom}
          onInsertVariableToPushTitle={this.onInsertVariableToPushTitle}
          onInsertVariableToPushBody={this.onInsertVariableToPushBody}
          onInsertVariableToSms={this.onInsertVariableToSms}
        />
      );
    }

    return (
      <GeneralMessageTextForm
        user={this.props.user}
        onEmailSubjectChange={this.onEmailSubjectChange}
        onEmailBodyChange={this.onEmailBodyChange}
        onSmsTextChange={this.onSmsTextChange}
        onPushTitleChange={this.onPushTitleChange}
        onPushBodyChange={this.onPushBodyChange}
        onEmailAttachmentChange={this.onEmailAttachmentChange}
        onRemoveAttachment={this.onRemoveAttachment}
        onCloseImageError={this.onCloseImageError}
        messageSmsText={messageSmsText}
        messageEmailSubject={messageEmailSubject}
        messageEmailBody={messageEmailBody}
        messagePushTitle={messagePushTitle}
        messagePushBody={messagePushBody}
        messageAttachments={messageAttachments}
        isImageError={isImageError}
        isError={isError}
        isLoadingFile={isLoadingFile}
        isEmailEditorEnabled={isEmailEditorEnabled}
        isSmsEditorEnabled={isSmsEditorEnabled}
        isPushEditorEnabled={isPushEditorEnabled}
        onIsEmailEditorChange={this.onIsEmailEditorChange}
        onIsSmsEditorChange={this.onIsSmsEditorChange}
        onIsPushEditorChange={this.onIsPushEditorChange}
        senderDisplayName={senderDisplayName}
        onSenderDisplayNameChange={this.onSenderDisplayNameChange}
        editorRef={undefined}
        subjectInputRef={undefined}
        fromInputRef={undefined}
        onInsertVariableToSubject={undefined}
        onInsertVariableToFrom={undefined}
      />
    );
  }

  renderMessagePreview(): React.ReactNode {
    const {
      messageEmailSubject,
      messageEmailBody,
      messagePushTitle,
      messagePushBody,
      messageSmsText,
      messageAttachments,
      senderDisplayName
    } = this.state;

    return (
      <GeneralMessagePreviewWithTabs
        messageSmsText={messageSmsText}
        messageEmailSubject={messageEmailSubject}
        messageEmailBody={messageEmailBody}
        messagePushTitle={messagePushTitle}
        messagePushBody={messagePushBody}
        messageAttachments={messageAttachments}
        senderDisplayName={senderDisplayName}
        hideRecipients={true}
        isEmailEnabled={this.state.isEmailEditorEnabled}
        isPushEnabled={this.state.isPushEditorEnabled}
        isSmsEnabled={this.state.isSmsEditorEnabled}
      />
    );
  }

  renderAffectedUsers(): React.ReactNode {
    const {
      affectedUserList,
      affectedUsersCount,
      affectedUserChannelSummary,
      isResolvingRecipients,
      isLoadingAffectedUserList
    } = this.state;

    return (
      <div className="gmAffectedScreen">
        <AffectedUserList
          affectedUsers={affectedUserList}
          affectedUsersCount={affectedUsersCount}
          channelSummary={affectedUserChannelSummary}
          isLoading={isResolvingRecipients}
          isLoadingAffectedUserList={isLoadingAffectedUserList}
          onLoadAffectedUsers={this.isGeneralMessagesV2Enabled() ? this.loadAffectedRecipientsList : undefined}
          isEmailExist={this.state.isEmailEditorEnabled}
          isPushExist={this.state.isPushEditorEnabled}
          isSmsExist={this.state.isSmsEditorEnabled}
          isModern={this.isGeneralMessagesV2Enabled()}
          hasChildVariables={this.hasChildVariablesInMessage()}
        />
      </div>
    );
  }
  renderSendingError(): React.ReactNode {
    const { errorMessage, isError } = this.state;

    if (!isError) {
      return null;
    }

    const isVersionConflict = /another browser|another tab|reload it/i.test(errorMessage);

    return (
      <div className="gmComposerInlineError" role="alert">
        <span className="gmComposerInlineError__icon" aria-hidden="true">
          !
        </span>
        <p>{errorMessage}</p>
        <div className="gmComposerInlineError__actions">
          {isVersionConflict && this.state.messageId ? (
            <button
              type="button"
              className="gmButton gmButton--secondary"
              onClick={() => this.loadDraftForEditing(this.state.messageId as string)}
            >
              Reload draft
            </button>
          ) : null}
          <button
            type="button"
            className="gmComposerInlineError__close"
            aria-label="Close"
            onClick={event => this.onCloseErrorClick(event)}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  renderChildVariableAudienceWarning(): React.ReactNode {
    const warning = this.getChildVariableAudienceWarning();

    if (!warning) return null;

    return (
      <div className="gmBanner gmBanner--warning gmChildVariableWarning" role="status">
        <strong>Check personalisation</strong>
        <span>{warning}</span>
      </div>
    );
  }

  getHeaderAudienceLabels(): string[] {
    const {
      isSendParents,
      isSendStudents,
      isSendStaff,
      isSendGovernors,
      isAllUserRecipientsPreselected,
      allUserAudienceGroups
    } = this.state;
    const usePresetAudience = isAllUserRecipientsPreselected;
    const labels: string[] = [];

    if (usePresetAudience ? allUserAudienceGroups.parents : isSendParents) labels.push('Parents');
    if (usePresetAudience ? allUserAudienceGroups.students : isSendStudents) labels.push('Students');
    if (usePresetAudience ? allUserAudienceGroups.staffs : isSendStaff) labels.push('School staff');
    if (usePresetAudience ? allUserAudienceGroups.governors : isSendGovernors) labels.push('Trustees / governors');

    return labels;
  }

  getHeaderParentPriorityLabel(): string {
    if (!this.state.isSendParents || this.state.isAllUserRecipientsPreselected) return '';

    const { all, firstPriority, secondPriority, thirdPriority } = this.state.parentsDeliverySettings;
    if (all) return 'All parents';

    const priorities = [firstPriority ? '1' : '', secondPriority ? '2' : '', thirdPriority ? '3' : ''].filter(Boolean);

    return priorities.length > 0 ? `Parent priority: ${priorities.join(', ')}` : '';
  }

  getHeaderScheduleLabel(): string {
    if (!this.state.messageScheduleAt) return '';

    const scheduledAt = new Date(this.state.messageScheduleAt);
    if (Number.isNaN(scheduledAt.getTime())) return '';

    return `Scheduled: ${scheduledAt.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  }

  renderCreateHeader(): React.ReactNode {
    const currentStep = this.getCurrentStepIndex();
    const stepCount = this.getSteps().length;
    const audienceLabels = this.getHeaderAudienceLabels();
    const channels = [
      this.state.isEmailEditorEnabled ? 'Email' : '',
      this.state.isPushEditorEnabled ? 'Push' : '',
      this.state.isSmsEditorEnabled ? 'SMS' : ''
    ].filter(Boolean);
    const title = this.state.messageTitle.trim() || (this.state.isEditMode ? 'Edit draft' : 'Create general message');
    const subject = this.state.messageEmailSubject.trim();
    const parentPriority = this.getHeaderParentPriorityLabel();
    const scheduledAt = this.getHeaderScheduleLabel();
    const attachmentCount = this.state.messageAttachments.length;
    const resolvedRecipients = Math.max(this.state.affectedUsersCount, this.state.affectedUserList.length);

    return (
      <header className="gmCreateHeader" data-help-anchor="gm-create-header">
        <button type="button" className="gmCreateHeader__back" onClick={this.onCancelClick}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Messages
        </button>
        <i aria-hidden="true" />
        <div className="gmCreateHeader__main">
          <h1 title={title}>{title}</h1>
          <div className="gmCreateHeader__summary" aria-label="Message details entered so far">
            {audienceLabels.length > 0 ? (
              <span className="is-audience">Audience: {audienceLabels.join(', ')}</span>
            ) : (
              <span className="is-placeholder">Audience not selected</span>
            )}
            {parentPriority ? <span className="is-priority">{parentPriority}</span> : null}
            {channels.length > 0 ? <span className="is-channels">Channels: {channels.join(', ')}</span> : null}
            {subject ? (
              <span className="is-subject" title={subject}>
                Subject: {subject}
              </span>
            ) : null}
            {resolvedRecipients > 0 ? <span className="is-recipients">Recipients: {resolvedRecipients}</span> : null}
            {attachmentCount > 0 ? (
              <span className="is-attachments">
                {attachmentCount} attachment{attachmentCount === 1 ? '' : 's'}
              </span>
            ) : null}
            {scheduledAt ? <span className="is-schedule">{scheduledAt}</span> : null}
          </div>
        </div>
        <div className="gmCreateHeader__status">
          {this.state.lastSavedAt ? (
            <span className="gmCreateHeader__saved">
              Saved{' '}
              {new Date(this.state.lastSavedAt).toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          ) : null}
          <span className="gmCreateHeader__step">
            Step {currentStep + 1} of {stepCount}
          </span>
        </div>
      </header>
    );
  }
  renderProgressBar() {
    const currentStep = this.getCurrentStepIndex() + 1;
    const totalSteps = this.getSteps().length;
    const progress = (currentStep / totalSteps) * 100;

    return <ProgressBar progress={progress} step={currentStep} stepTotal={totalSteps} />;
  }

  render() {
    if (!this.isGeneralMessagesEnabledForSchool()) {
      return (
        <GeneralMessagesUnavailableModal
          isOpen={true}
          onClose={this.closeUnavailableModal}
          onViewModule={() => this.props.history.push('/school/modules')}
        />
      );
    }

    const stepCount = this.getSteps().length;
    const currentStep = this.getCurrentStepIndex();
    const { isError } = this.state;
    const hasNext = currentStep + 1 < stepCount;
    const hasPrev = currentStep > 0;
    const isNextButtonDisabled = this.isNextButtonDisabled();
    const isAffectedUsersStep = this.getSteps()[currentStep] === MESSAGE_CREATING_STEPS.AFFECTED_USERS;
    return (
      <div className="bCreateGeneralMessagesView">
        <UnsavedChangesModal
          isOpen={this.state.isUnsavedChangesModalOpen}
          message="Your latest changes to this general message have not been saved."
          onKeepEditing={this.onKeepEditing}
          onDiscard={this.onDiscardChanges}
        />
        {this.renderCreateHeader()}
        <div className="gmComposerShell">
          <div className={`gmComposerCard ${isAffectedUsersStep ? 'gmComposerCard--recipients' : ''}`}>
            <div className="gmComposerCard__body">
              {this.state.isInitialLoading ? (
                <div className="gmComposerLoading" role="status">
                  {this.state.isEditMode ? 'Loading draft…' : 'Preparing copied message…'}
                </div>
              ) : (
                <React.Fragment>
                  {this.state.isScheduleFormVisible && (
                    <ScheduleSendingForm onScheduleDateChange={this.onScheduleDateChange} autoFocus />
                  )}
                  <div data-help-anchor="gm-create-step">{this.renderViewByStep()}</div>
                  {this.renderChildVariableAudienceWarning()}
                  {this.renderSendingError()}

                  <div data-help-anchor="gm-create-actions">
                    <CreateGeneralMessageButtons
                      hasNext={hasNext}
                      hasPrevious={hasPrev}
                      onNextClick={this.onNextClick}
                      onPreviousClick={this.onPrevClick}
                      onSaveClick={this.onSaveClick}
                      onSendClick={this.onSendClick}
                      onScheduleClick={this.onScheduleClick}
                      onActiveActionChange={this.onActiveActionChange}
                      onCancelClick={this.onCancelClick}
                      isError={isError}
                      isNextButtonDisabled={isNextButtonDisabled}
                      isLoading={this.state.isActionInProgress || this.state.isLoadingFile}
                      isScheduleFormVisible={this.state.isScheduleFormVisible}
                    />
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
